import React, { useState, useEffect, useRef } from 'react';
import { Wrench, Package, ChevronDown, ChevronRight, Printer, IndianRupee, Scale, Hash } from 'lucide-react';
import { toast } from 'sonner';
import fetchWithAuth from '../../services/fetchWithAuth';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('access_token');

const formatINR = (v) => v != null ? Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';
const formatWeight = (kg) => {
  if (!kg || kg === 0) return '0 g';
  if (kg >= 1) return `${kg.toFixed(3)} kg`;
  return `${(kg * 1000).toFixed(1)} g`;
};

const CategoryBadge = ({ category }) => {
  const colors = {
    Bolt: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    Nut: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    Washer: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  };
  return (
    <span className={`px-1.5 py-0.5 rounded text-[8px] font-medium border ${colors[category] || 'bg-slate-500/20 text-slate-300 border-slate-500/30'}`}>
      {category}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/* Print Overlay                                                       */
/* ------------------------------------------------------------------ */
const PrintOverlay = ({ data, onClose }) => {
  const printRef = useRef(null);
  const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Fastener Procurement List</title>
<style>* { margin: 0; padding: 0; box-sizing: border-box; } body { font-family: monospace; color: #222; padding: 24px; font-size: 12px; } @media print { body { padding: 12px; } }</style>
</head><body>${content.innerHTML}</body></html>`);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 300);
  };

  const s = {
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4, paddingBottom: 0 },
    h1: { fontSize: 16, fontWeight: 700, margin: 0, color: '#222' },
    subtitle: { fontSize: 11, color: '#666', marginTop: 4 },
    meta: { textAlign: 'right', fontSize: 11, color: '#666', lineHeight: 1.7 },
    metaBold: { fontWeight: 700 },
    separator: { borderTop: '1px solid #ccc', marginTop: 10, marginBottom: 14 },
    row: { display: 'flex', gap: 12, marginBottom: 14 },
    card: { flex: 1, border: '1px solid #ddd', borderRadius: 0, padding: '8px 10px' },
    cardLabel: { fontSize: 8, textTransform: 'uppercase', letterSpacing: 0.5, color: '#666', marginBottom: 2 },
    cardValue: { fontSize: 14, fontWeight: 700, color: '#222' },
    groupHdr: { background: '#e8f0f8', padding: '5px 8px', fontWeight: 700, fontSize: 13, borderBottom: '1px solid #ccc', display: 'flex', justifyContent: 'space-between', marginTop: 12 },
    matHdr: { background: '#f5f5f5', padding: '4px 8px 4px 12px', fontSize: 11, color: '#666', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between' },
    table: { width: '100%', borderCollapse: 'collapse', marginBottom: 0, fontSize: 11 },
    th: { background: '#f5f5f5', textAlign: 'left', padding: '4px 8px', border: '1px solid #ddd', fontWeight: 700, fontSize: 10, textTransform: 'uppercase' },
    thR: { background: '#f5f5f5', textAlign: 'right', padding: '4px 8px', border: '1px solid #ddd', fontWeight: 700, fontSize: 10, textTransform: 'uppercase' },
    td: { padding: '4px 8px', border: '1px solid #ddd', color: '#222' },
    tdR: { padding: '4px 8px', border: '1px solid #ddd', textAlign: 'right', color: '#222' },
    tdCat: { padding: '4px 8px', border: '1px solid #ddd', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#222' },
    totalTd: { fontWeight: 700, background: '#f0f0f0', borderTop: '2px solid #333', padding: '4px 8px', border: '1px solid #ddd', color: '#222' },
    totalTdR: { fontWeight: 700, background: '#f0f0f0', borderTop: '2px solid #333', padding: '4px 8px', border: '1px solid #ddd', textAlign: 'right', color: '#222' },
    footer: { marginTop: 20, borderTop: '1px solid #ccc', paddingTop: 10, display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#666' },
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-lg w-[880px] max-h-[85vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-slate-100 border-b">
          <span className="text-sm font-semibold text-slate-800">Fastener Procurement List — Print Preview</span>
          <div className="flex gap-2">
            <button onClick={handlePrint} data-testid="print-confirm-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white rounded text-xs font-medium hover:bg-slate-700 transition-colors">
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
            <button onClick={onClose}
              className="px-3 py-1.5 bg-slate-200 text-slate-700 rounded text-xs font-medium hover:bg-slate-300 transition-colors">
              Close
            </button>
          </div>
        </div>
        {/* Print Content */}
        <div className="overflow-y-auto max-h-[calc(85vh-48px)] p-5 bg-white">
          <div ref={printRef} style={{ fontFamily: 'monospace', fontSize: 12, color: '#222' }}>
            {/* Header */}
            <div style={s.header}>
              <div>
                <h1 style={s.h1}>FASTENER PROCUREMENT LIST</h1>
                <div style={s.subtitle}>{data.bom_name || 'BOM'}</div>
              </div>
              <div style={s.meta}>
                <div>Revision: <span style={s.metaBold}>{data.revision_label || 'A.1'}</span></div>
                <div>Date: {today}</div>
                <div>{data.total_fasteners} line items &middot; {data.total_quantity} total pcs</div>
              </div>
            </div>
            <div style={s.separator} />

            {/* Summary Cards */}
            <div style={s.row}>
              <div style={s.card}><div style={s.cardLabel}>Total Lines</div><div style={s.cardValue}>{data.total_fasteners}</div></div>
              <div style={s.card}><div style={s.cardLabel}>Total Quantity</div><div style={s.cardValue}>{data.total_quantity} pcs</div></div>
              <div style={s.card}><div style={s.cardLabel}>Total Cost</div><div style={s.cardValue}>{'\u20B9'}{formatINR(data.total_cost)}</div></div>
              <div style={s.card}><div style={s.cardLabel}>Total Weight</div><div style={s.cardValue}>{formatWeight(data.total_weight_kg)}</div></div>
            </div>

            {/* Grouped Tables */}
            {data.groups.map(group => (
              <div key={group.size} style={{ marginBottom: 8 }}>
                <div style={s.groupHdr}>
                  <span>{group.size} — {group.total_qty} pcs</span>
                  <span>{'\u20B9'}{formatINR(group.total_cost)} &middot; {formatWeight(group.total_weight_kg)}</span>
                </div>
                {group.materials.map(mat => (
                  <div key={mat.material}>
                    <div style={s.matHdr}>
                      <span>{mat.material} — {mat.subtotal_qty} pcs</span>
                      <span>{'\u20B9'}{formatINR(mat.subtotal_cost)} &middot; {formatWeight(mat.subtotal_weight_kg)}</span>
                    </div>
                    <table style={s.table}>
                      <thead>
                        <tr>
                          <th style={{ ...s.th, width: 58 }}>Part #</th>
                          <th style={{ ...s.th, width: 48 }}>Type</th>
                          <th style={s.th}>Description</th>
                          <th style={{ ...s.th, width: 42 }}>Len</th>
                          <th style={{ ...s.th, width: 68 }}>Standard</th>
                          <th style={{ ...s.thR, width: 30 }}>Qty</th>
                          <th style={{ ...s.thR, width: 62 }}>Unit Cost</th>
                          <th style={{ ...s.thR, width: 68 }}>Ext Cost</th>
                          <th style={{ ...s.thR, width: 52 }}>Unit Wt</th>
                          <th style={{ ...s.thR, width: 52 }}>Ext Wt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mat.items.map((item, idx) => (
                          <tr key={item.line_id || idx}>
                            <td style={{ ...s.td, fontSize: 9 }}>{item.part_number}</td>
                            <td style={s.tdCat}>{item.category}</td>
                            <td style={s.td}>{item.sub_type_name || item.description}</td>
                            <td style={s.td}>{item.length_mm ? `${item.length_mm}mm` : '-'}</td>
                            <td style={s.td}>{item.standard || '-'}</td>
                            <td style={s.tdR}>{item.quantity}</td>
                            <td style={s.tdR}>{'\u20B9'}{formatINR(item.unit_cost)}</td>
                            <td style={s.tdR}>{'\u20B9'}{formatINR(item.extended_cost)}</td>
                            <td style={s.tdR}>{formatWeight(item.unit_weight_kg)}</td>
                            <td style={s.tdR}>{formatWeight(item.extended_weight_kg)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            ))}

            {/* Grand Totals */}
            <table style={s.table}>
              <tbody>
                <tr>
                  <td style={{ ...s.totalTd, textAlign: 'right', paddingRight: 12 }} colSpan={5}>GRAND TOTAL</td>
                  <td style={{ ...s.totalTdR, width: 30 }}>{data.total_quantity}</td>
                  <td style={{ ...s.totalTdR, width: 62 }}></td>
                  <td style={{ ...s.totalTdR, width: 68 }}>{'\u20B9'}{formatINR(data.total_cost)}</td>
                  <td style={{ ...s.totalTdR, width: 52 }}></td>
                  <td style={{ ...s.totalTdR, width: 52 }}>{formatWeight(data.total_weight_kg)}</td>
                </tr>
              </tbody>
            </table>

            {/* Footer */}
            <div style={s.footer}>
              <span>Generated from HADEN ATLAS PLM</span>
              <span>Printed: {today}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Main Panel                                                          */
/* ------------------------------------------------------------------ */
export const FastenerSummaryPanel = ({ bomId, revisionId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [showPrint, setShowPrint] = useState(false);

  useEffect(() => {
    if (!bomId || !revisionId) return;
    const tk = getToken();
    setLoading(true);
    fetchWithAuth(`${API}/api/v1/bom/${bomId}/revisions/${revisionId}/fastener-summary`, {
      headers: { Authorization: `Bearer ${tk}` },
    })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { toast.error('Failed to load fastener summary'); setLoading(false); });
  }, [bomId, revisionId]);

  const toggleGroup = (key) => setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));

  if (loading) return (
    <div className="px-5 pb-3 text-[10px] text-slate-500 animate-pulse">Loading fastener summary...</div>
  );

  if (!data || data.total_fasteners === 0) return (
    <div className="px-5 pb-3 text-[10px] text-slate-500 text-center py-4">
      No fasteners in this BOM. Use the "+ Fastener" button to add fasteners.
    </div>
  );

  return (
    <div className="px-5 pb-3 pt-2 border-t border-slate-700/30 animate-in fade-in duration-200" data-testid="fastener-summary-panel">
      {/* Summary Cards Row */}
      <div className="flex items-stretch gap-2.5 mb-3">
        {/* Total Lines */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-amber-500/10 border border-amber-500/20 min-w-[100px]">
          <Wrench className="w-3.5 h-3.5 text-amber-400 shrink-0" />
          <div>
            <div className="text-[8px] text-amber-400/70 uppercase tracking-wider font-medium">Lines</div>
            <div className="text-sm font-mono font-bold text-amber-300" data-testid="fastener-total-lines">{data.total_fasteners}</div>
          </div>
        </div>
        {/* Total Qty */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-blue-500/10 border border-blue-500/20 min-w-[100px]">
          <Hash className="w-3.5 h-3.5 text-blue-400 shrink-0" />
          <div>
            <div className="text-[8px] text-blue-400/70 uppercase tracking-wider font-medium">Total Qty</div>
            <div className="text-sm font-mono font-bold text-blue-300" data-testid="fastener-total-qty">{data.total_quantity}</div>
          </div>
        </div>
        {/* Total Cost */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-500/10 border border-emerald-500/20 min-w-[130px]">
          <IndianRupee className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
          <div>
            <div className="text-[8px] text-emerald-400/70 uppercase tracking-wider font-medium">Total Cost</div>
            <div className="text-[13px] font-mono font-bold text-emerald-300" data-testid="fastener-total-cost">
              {'\u20B9'}{formatINR(data.total_cost)}
            </div>
          </div>
        </div>
        {/* Total Weight */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-violet-500/10 border border-violet-500/20 min-w-[120px]">
          <Scale className="w-3.5 h-3.5 text-violet-400 shrink-0" />
          <div>
            <div className="text-[8px] text-violet-400/70 uppercase tracking-wider font-medium">Total Weight</div>
            <div className="text-[13px] font-mono font-bold text-violet-300" data-testid="fastener-total-weight">
              {formatWeight(data.total_weight_kg)}
            </div>
          </div>
        </div>
        {/* Unique Types */}
        <div className="px-3 py-2 rounded-md bg-slate-700/30 border border-slate-600/20">
          <div className="text-[8px] text-slate-500 uppercase tracking-wider font-medium">Types</div>
          <div className="text-sm font-mono font-bold text-slate-300" data-testid="fastener-unique-types">{data.unique_types}</div>
        </div>
        {/* Sizes */}
        <div className="px-3 py-2 rounded-md bg-slate-700/30 border border-slate-600/20">
          <div className="text-[8px] text-slate-500 uppercase tracking-wider font-medium">Sizes</div>
          <div className="text-[10px] font-mono text-slate-300 mt-0.5">{data.sizes_used.join(', ')}</div>
        </div>
        {/* Materials */}
        <div className="px-3 py-2 rounded-md bg-slate-700/30 border border-slate-600/20 max-w-[180px]">
          <div className="text-[8px] text-slate-500 uppercase tracking-wider font-medium">Materials</div>
          <div className="text-[10px] text-slate-300 truncate mt-0.5">{data.materials_used.join(', ')}</div>
        </div>

        {/* Print Button — pushed to right */}
        <div className="ml-auto flex items-center">
          <button onClick={() => setShowPrint(true)} data-testid="fastener-print-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-700/60 border border-slate-600/30 text-slate-300 hover:bg-slate-600/60 hover:text-white transition-colors text-[10px] font-medium">
            <Printer className="w-3.5 h-3.5" /> Print List
          </button>
        </div>
      </div>

      {/* Grouped Table + Kit Sets side by side */}
      <div className="flex gap-4">
        {/* Left: Grouped by Size */}
        <div className="flex-1 min-w-0">
          <div className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold mb-1">By Size / Material</div>
          <div className="border border-slate-700/40 rounded overflow-hidden max-h-[220px] overflow-y-auto">
            {data.groups.map(group => {
              const key = group.size;
              const expanded = expandedGroups[key] !== false;
              return (
                <div key={key} data-testid={`fastener-group-${key}`}>
                  <button onClick={() => toggleGroup(key)}
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left bg-slate-800/50 hover:bg-slate-700/50 border-b border-slate-700/30 transition-colors">
                    {expanded ? <ChevronDown className="w-3 h-3 text-slate-500" /> : <ChevronRight className="w-3 h-3 text-slate-500" />}
                    <span className="text-[11px] font-mono font-bold text-amber-300">{group.size}</span>
                    <span className="text-[9px] text-slate-500">{group.materials.length} material{group.materials.length > 1 ? 's' : ''}</span>
                    <span className="ml-auto flex items-center gap-3 text-[10px] font-mono">
                      <span className="text-slate-400">{group.total_qty} pcs</span>
                      <span className="text-emerald-400/80">{'\u20B9'}{formatINR(group.total_cost)}</span>
                      <span className="text-violet-400/80">{formatWeight(group.total_weight_kg)}</span>
                    </span>
                  </button>
                  {expanded && group.materials.map(mat => (
                    <div key={mat.material} className="border-b border-slate-700/20">
                      <div className="flex items-center gap-2 px-3 py-1 bg-slate-900/30">
                        <span className="text-[10px] text-slate-400 font-medium">{mat.material}</span>
                        <span className="text-[9px] text-slate-600">{mat.items.length} line{mat.items.length > 1 ? 's' : ''}</span>
                        <span className="ml-auto flex items-center gap-3 text-[9px] font-mono">
                          <span className="text-slate-500">{mat.subtotal_qty} pcs</span>
                          <span className="text-emerald-500/70">{'\u20B9'}{formatINR(mat.subtotal_cost)}</span>
                          <span className="text-violet-500/70">{formatWeight(mat.subtotal_weight_kg)}</span>
                        </span>
                      </div>
                      {mat.items.map((item, idx) => (
                        <div key={item.line_id || idx} className="flex items-center gap-2 px-4 py-0.5 text-[9px] hover:bg-slate-800/30 group">
                          <span className="font-mono text-slate-500 w-10 shrink-0">{item.part_number}</span>
                          <CategoryBadge category={item.category} />
                          <span className="text-slate-300 truncate flex-1 min-w-0">{item.sub_type_name || item.description}</span>
                          {item.length_mm && <span className="text-slate-600 shrink-0">{item.length_mm}mm</span>}
                          {item.standard && <span className="text-slate-600 shrink-0">[{item.standard}]</span>}
                          <span className="font-mono text-slate-400 shrink-0 w-6 text-right">x{item.quantity}</span>
                          <span className="font-mono text-emerald-400/70 shrink-0 w-16 text-right">{'\u20B9'}{formatINR(item.extended_cost)}</span>
                          <span className="font-mono text-violet-400/60 shrink-0 w-14 text-right">{formatWeight(item.extended_weight_kg)}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Kit Assembly Sets */}
        {data.kit_sets.length > 0 && (
          <div className="w-[280px] shrink-0">
            <div className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold mb-1">Kit Assemblies</div>
            <div className="border border-slate-700/40 rounded overflow-hidden max-h-[220px] overflow-y-auto">
              {data.kit_sets.map((kit, idx) => {
                const bolts = kit.components.filter(c => c.category === 'Bolt');
                const nuts = kit.components.filter(c => c.category === 'Nut');
                const washers = kit.components.filter(c => c.category === 'Washer');
                return (
                  <div key={idx} className="px-2.5 py-2 border-b border-slate-700/20 hover:bg-slate-800/30"
                    data-testid={`fastener-kit-${idx}`}>
                    <div className="flex items-center gap-1.5 mb-1">
                      <Package className="w-3 h-3 text-emerald-400" />
                      <span className="text-[10px] font-bold text-white">{kit.size} {kit.material}</span>
                    </div>
                    <div className="flex gap-2 text-[9px] mb-1">
                      {bolts.length > 0 && <span className="text-amber-400">{bolts.length} bolt{bolts.length > 1 ? 's' : ''}</span>}
                      {nuts.length > 0 && <span className="text-blue-400">{nuts.length} nut{nuts.length > 1 ? 's' : ''}</span>}
                      {washers.length > 0 && <span className="text-emerald-400">{washers.length} washer{washers.length > 1 ? 's' : ''}</span>}
                      <span className="ml-auto font-mono text-slate-500">{kit.total_qty} pcs</span>
                    </div>
                    <div className="flex gap-3 text-[9px] font-mono border-t border-slate-700/20 pt-1">
                      <span className="text-emerald-400/70">{'\u20B9'}{formatINR(kit.total_cost)}</span>
                      <span className="text-violet-400/70">{formatWeight(kit.total_weight_kg)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Print Overlay */}
      {showPrint && <PrintOverlay data={data} onClose={() => setShowPrint(false)} />}
    </div>
  );
};
